// =============================================================================
// Origin UI — src/pages/reports/ReportsPage.tsx
// Reporting & Audit screen for Admin + Auditor.
// Three export sections: Requests, Tasks, Audit Events — each with filters
// and a download button that triggers a CSV file download.
// =============================================================================

import { useState, useEffect } from 'react';
import { referenceApi } from '@/services/requestApi';
import { PageHeader, Card, ErrorBanner } from '@/components/common';
import { apiRequest } from '@/services/apiService';
import type { DepartmentRef } from '@/types/api';
import styles from './reports.module.css';

export function ReportsPage() {
  const [departments, setDepartments] = useState<DepartmentRef[]>([]);
  const [error, setError] = useState('');

  // Requests filters
  const [reqDateFrom, setReqDateFrom] = useState('');
  const [reqDateTo, setReqDateTo] = useState('');
  const [reqStatus, setReqStatus] = useState('');
  const [reqType, setReqType] = useState('');
  const [reqDept, setReqDept] = useState('');
  const [reqDownloading, setReqDownloading] = useState(false);

  // Tasks filters
  const [taskDateFrom, setTaskDateFrom] = useState('');
  const [taskDateTo, setTaskDateTo] = useState('');
  const [taskStatus, setTaskStatus] = useState('');
  const [taskDownloading, setTaskDownloading] = useState(false);

  // Audit filters
  const [auditDateFrom, setAuditDateFrom] = useState('');
  const [auditDateTo, setAuditDateTo] = useState('');
  const [auditDownloading, setAuditDownloading] = useState(false);

  useEffect(() => {
    referenceApi.departments().then(setDepartments).catch(() => {});
  }, []);

  async function downloadCsv(path: string, setDownloading: (v: boolean) => void) {
    try {
      setDownloading(true);
      setError('');

      // Use apiRequest with raw response handling for file download
      const response = await apiRequest<Blob>(path, {
        method: 'GET',
        headers: { Accept: 'text/csv' },
      });

      // Create download link
      const blob = new Blob([response as any], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Extract filename from path
      const filename = path.split('/').pop()?.split('?')[0] || 'export.csv';
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  }

  function buildQueryString(params: Record<string, string>): string {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) query.set(k, v); });
    const qs = query.toString();
    return qs ? `?${qs}` : '';
  }

  function handleRequestsExport() {
    const qs = buildQueryString({
      dateFrom: reqDateFrom,
      dateTo: reqDateTo,
      status: reqStatus,
      requestType: reqType,
      department: reqDept,
    });
    downloadCsv(`/api/reports/requests.csv${qs}`, setReqDownloading);
  }

  function handleTasksExport() {
    const qs = buildQueryString({
      dateFrom: taskDateFrom,
      dateTo: taskDateTo,
      status: taskStatus,
    });
    downloadCsv(`/api/reports/tasks.csv${qs}`, setTaskDownloading);
  }

  function handleAuditExport() {
    const qs = buildQueryString({
      dateFrom: auditDateFrom,
      dateTo: auditDateTo,
    });
    downloadCsv(`/api/reports/audit.csv${qs}`, setAuditDownloading);
  }

  return (
    <div>
      <PageHeader
        title="Reports & Audit"
        subtitle="Export request, task, and audit data as CSV files"
      />

      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      {/* ─── Requests Export ─── */}
      <Card className={styles.exportCard}>
        <div className={styles.exportHeader}>
          <div>
            <h3 className={styles.exportTitle}>📋 Requests Export</h3>
            <p className={styles.exportDesc}>
              Export all requests with employee details, status, and timestamps.
              Admin users will also see soft-deleted records.
            </p>
          </div>
          <button
            className={styles.exportBtn}
            onClick={handleRequestsExport}
            disabled={reqDownloading}
          >
            {reqDownloading ? 'Downloading...' : 'Download CSV'}
          </button>
        </div>
        <div className={styles.filterGrid}>
          <FilterField label="From Date" type="date" value={reqDateFrom} onChange={setReqDateFrom} />
          <FilterField label="To Date" type="date" value={reqDateTo} onChange={setReqDateTo} />
          <FilterField label="Status" type="select" value={reqStatus} onChange={setReqStatus}
            options={[
              { value: '', label: 'All statuses' },
              { value: 'Draft', label: 'Draft' },
              { value: 'Submitted', label: 'Submitted' },
              { value: 'Returned', label: 'Returned' },
              { value: 'Approved', label: 'Approved' },
              { value: 'TasksInProgress', label: 'Tasks In Progress' },
              { value: 'Completed', label: 'Completed' },
              { value: 'Cancelled', label: 'Cancelled' },
            ]}
          />
          <FilterField label="Request Type" type="select" value={reqType} onChange={setReqType}
            options={[
              { value: '', label: 'All types' },
              { value: 'Onboarding', label: 'Onboarding' },
              { value: 'Offboarding', label: 'Offboarding' },
            ]}
          />
          <FilterField label="Department" type="select" value={reqDept} onChange={setReqDept}
            options={[
              { value: '', label: 'All departments' },
              ...departments.map(d => ({ value: d.name, label: d.name })),
            ]}
          />
        </div>
      </Card>

      {/* ─── Tasks Export ─── */}
      <Card className={styles.exportCard}>
        <div className={styles.exportHeader}>
          <div>
            <h3 className={styles.exportTitle}>📦 Tasks Export</h3>
            <p className={styles.exportDesc}>
              Export all IT execution tasks with assignment, status, and evidence notes.
            </p>
          </div>
          <button
            className={styles.exportBtn}
            onClick={handleTasksExport}
            disabled={taskDownloading}
          >
            {taskDownloading ? 'Downloading...' : 'Download CSV'}
          </button>
        </div>
        <div className={styles.filterGrid}>
          <FilterField label="From Date" type="date" value={taskDateFrom} onChange={setTaskDateFrom} />
          <FilterField label="To Date" type="date" value={taskDateTo} onChange={setTaskDateTo} />
          <FilterField label="Status" type="select" value={taskStatus} onChange={setTaskStatus}
            options={[
              { value: '', label: 'All statuses' },
              { value: 'Pending', label: 'Pending' },
              { value: 'InProgress', label: 'In Progress' },
              { value: 'Completed', label: 'Completed' },
              { value: 'Blocked', label: 'Blocked' },
            ]}
          />
        </div>
      </Card>

      {/* ─── Audit Export ─── */}
      <Card className={styles.exportCard}>
        <div className={styles.exportHeader}>
          <div>
            <h3 className={styles.exportTitle}>🔒 Audit Trail Export</h3>
            <p className={styles.exportDesc}>
              Export the immutable audit trail including all state transitions, edits, and significant actions.
            </p>
          </div>
          <button
            className={styles.exportBtn}
            onClick={handleAuditExport}
            disabled={auditDownloading}
          >
            {auditDownloading ? 'Downloading...' : 'Download CSV'}
          </button>
        </div>
        <div className={styles.filterGrid}>
          <FilterField label="From Date" type="date" value={auditDateFrom} onChange={setAuditDateFrom} />
          <FilterField label="To Date" type="date" value={auditDateTo} onChange={setAuditDateTo} />
        </div>
      </Card>
    </div>
  );
}

// ─── Filter Field Component ───

function FilterField({
  label,
  type,
  value,
  onChange,
  options,
}: {
  label: string;
  type: 'date' | 'select';
  value: string;
  onChange: (v: string) => void;
  options?: { value: string; label: string }[];
}) {
  return (
    <div className={styles.filterField}>
      <label className={styles.filterLabel}>{label}</label>
      {type === 'select' && options ? (
        <select
          className={styles.filterInput}
          value={value}
          onChange={e => onChange(e.target.value)}
        >
          {options.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ) : (
        <input
          className={styles.filterInput}
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
        />
      )}
    </div>
  );
}
